import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { localToday } from '../utils/dateUtils';
import { emitTareaChanged } from '../utils/tareasBus';
import { onDataChanged } from '../utils/dataBus';

const formatDateLong = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const formatDateShort = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';

function daysUntil(fecha, today) {
  return Math.round((new Date(fecha + 'T12:00:00') - new Date(today + 'T12:00:00')) / 86400000);
}

function urgenciaStyle(days) {
  if (days < 0)  return { label: `Vencido hace ${Math.abs(days)} día${Math.abs(days) > 1 ? 's' : ''}`, color: '#ef4444' };
  if (days === 0) return { label: 'Hoy', color: '#ef4444' };
  if (days === 1) return { label: 'Mañana', color: '#f59e0b' };
  if (days <= 7)  return { label: `En ${days} días`, color: '#f59e0b' };
  return { label: formatDateShort(null), color: 'var(--text4)' };
}

function clientesDeExpediente(exp) {
  if (!exp?.expedientes_clientes?.length) return '';
  return exp.expedientes_clientes.map(ec => `${ec.clientes?.apellido || ''} ${ec.clientes?.nombre || ''}`.trim()).filter(Boolean).join(', ');
}

function GrupoVencimientos({ fecha, items, today, onOpenEdit, onVerTarea, onNavigateExpediente, completarTarea }) {
  const [open, setOpen] = useState(true);
  const days   = daysUntil(fecha, today);
  const urg    = urgenciaStyle(days);
  const vencido = days < 0;
  return (
    <div style={{ border: `1px solid ${vencido ? '#fecaca' : 'var(--border2)'}`, borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={() => setOpen(v => !v)}
        style={{ padding: '10px 16px', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', borderBottom: open ? `1px solid ${vencido ? '#fecaca' : 'var(--border2)'}` : 'none' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize', letterSpacing: 0.5 }}>{formatDateLong(fecha)}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: urg.color, background: urg.color + '18', padding: '1px 8px', borderRadius: 10 }}>{urg.label}</span>
        <span style={{ fontSize: 11, color: 'var(--text4)', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, padding: '0 7px', marginLeft: 'auto' }}>{items.length}</span>
        <span style={{ fontSize: 11, color: 'var(--text4)' }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && items.map(en => {
        const t          = en.raw;
        const expLabel   = t.expedientes?.caratula;
        const clienteTxt = en.kind === 'evento'
          ? (t.clientes ? `${t.clientes.apellido || ''} ${t.clientes.nombre || ''}`.trim() : t.cliente_manual)
          : clientesDeExpediente(t.expedientes);
        return (
          <div key={en.id}
            onClick={() => en.kind === 'evento' ? onOpenEdit?.(t) : onVerTarea?.(t)}
            style={{ padding: '10px 14px', display: 'flex', gap: 11, alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--border2)', background: 'var(--surface)' }}>
            <div style={{ width: 3, alignSelf: 'stretch', background: urg.color, borderRadius: 2, flexShrink: 0 }} />
            {en.kind === 'tarea' && (
              <input type="checkbox" title="Marcar como completada"
                style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
                onClick={e => e.stopPropagation()}
                onChange={() => completarTarea(t.id)} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>{t.titulo}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: en.kind === 'tarea' ? '#64748b22' : '#f59e0b22', color: en.kind === 'tarea' ? '#64748b' : '#f59e0b' }}>
                  {en.kind === 'tarea' ? 'Tarea' : t.tipo}
                </span>
                {expLabel && (
                  <span onClick={e => { e.stopPropagation(); onNavigateExpediente?.(t.expediente_id); }}
                    style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }}>
                    {expLabel.substring(0, 35)}
                  </span>
                )}
                {clienteTxt && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{clienteTxt.substring(0, 30)}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Props:
//   expedienteId, clienteId  — filtros opcionales
//   mode: 'page' | 'widget'
//   limit — max items en widget
//   onOpenEdit(ev), onVerTarea(tarea), onNavigateExpediente(id)
export default function VencimientosPanel({
  expedienteId = null,
  clienteId = null,
  mode = 'page',
  limit = 6,
  onOpenEdit,
  onVerTarea,
  onNavigateExpediente,
}) {
  const [eventos, setEventos] = useState([]);
  const [tareas, setTareas]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [vencidosOpen, setVencidosOpen] = useState(true);

  const today = localToday();

  const fetchData = useCallback(async () => {
    setLoading(true);
    let qEv = supabase.from('agenda')
      .select('*, clientes(nombre, apellido), expedientes(numero, caratula)')
      .in('tipo', ['Vencimiento procesal', 'Vencimiento de deuda'])
      .eq('estado', 'pendiente')
      .eq('eliminado', false)
      .order('fecha', { ascending: true });

    if (expedienteId) qEv = qEv.eq('expediente_id', expedienteId);
    if (clienteId)    qEv = qEv.eq('cliente_id', clienteId);

    let qT = supabase.from('tareas')
      .select('*, expedientes(caratula, numero, expedientes_clientes(clientes(nombre, apellido))), tramites(nombre)')
      .eq('completada', false)
      .eq('descartada', false)
      .eq('eliminado', false)
      .not('fecha_vencimiento', 'is', null)
      .order('fecha_vencimiento', { ascending: true });

    if (expedienteId) qT = qT.eq('expediente_id', expedienteId);

    const [{ data: evData }, { data: tData }] = await Promise.all([qEv, qT]);
    setEventos(evData || []);
    setTareas(tData || []);
    setLoading(false);
  }, [expedienteId, clienteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mantenerse sincronizado ante cualquier escritura en tareas o agenda,
  // venga de donde venga (otro componente, cascade de borrado, RPC).
  useEffect(() => onDataChanged(() => fetchData(), ['tareas', 'agenda']), [fetchData]);

  async function completarTarea(id) {
    await supabase.from('tareas').update({ completada: true, completada_en: new Date().toISOString() }).eq('id', id);
    setTareas(prev => prev.filter(t => t.id !== id));
    emitTareaChanged({ id, completada: true });
  }

  const entries = [
    ...eventos.map(e => ({ id: 'ev' + e.id, kind: 'evento', fecha: e.fecha, raw: e })),
    ...tareas.map(t  => ({ id: 'ta' + t.id, kind: 'tarea',  fecha: t.fecha_vencimiento, raw: t })),
  ].sort((a, b) => a.fecha.localeCompare(b.fecha));

  const vencidos = entries.filter(e => daysUntil(e.fecha, today) < 0).length;

  // ── WIDGET ──────────────────────────────────────────────────────────────────
  if (mode === 'widget') {
    if (loading) return <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Cargando…</div>;
    const items = entries.slice(0, limit);
    if (items.length === 0) return <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Sin vencimientos próximos</div>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {items.map(en => {
          const days = daysUntil(en.fecha, today);
          const urg  = urgenciaStyle(days);
          const t    = en.raw;
          const exp  = t.expedientes?.caratula?.substring(0, 24);
          return (
            <div key={en.id}
              onClick={() => en.kind === 'evento' ? onOpenEdit?.(t) : onVerTarea?.(t)}
              style={{ padding: '8px 11px', background: days < 0 ? '#fff8f8' : 'var(--surface)', border: `0.5px solid ${days < 0 ? '#fecaca' : 'var(--border)'}`, borderRadius: 8, borderLeft: `3px solid ${urg.color}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.titulo}</div>
                {exp && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 1 }}>{exp}</div>}
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: urg.color, flexShrink: 0 }}>{urg.label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // ── PAGE ─────────────────────────────────────────────────────────────────────
  const grupos = {};
  entries.forEach(en => { (grupos[en.fecha] = grupos[en.fecha] || []).push(en); });
  const fechas = Object.keys(grupos).sort();
  const fechasVencidas = fechas.filter(f => daysUntil(f, today) < 0);
  const fechasFuturas  = fechas.filter(f => daysUntil(f, today) >= 0);


  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text4)' }}>{entries.length} vencimientos</span>
        {vencidos > 0 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#ef4444', background: '#fee2e2', padding: '1px 8px', borderRadius: 10 }}>
            {vencidos} vencido{vencidos > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)' }}>Cargando…</div>
      ) : fechas.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✅</div>
          <div className="empty-state-title">Sin vencimientos</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Acordeón: card externa con todos los vencidos */}
          {fechasVencidas.length > 0 && (
            <div style={{ border: '1px solid #fecaca', borderRadius: 10, overflow: 'hidden' }}>
              <div onClick={() => setVencidosOpen(o => !o)}
                style={{ padding: '10px 14px', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', borderBottom: vencidosOpen ? '1px solid #fecaca' : 'none' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: 0.3 }}>Vencidos</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#ef4444', background: '#fee2e2', padding: '0 7px', borderRadius: 10 }}>{vencidos}</span>
                <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 'auto' }}>{vencidosOpen ? '▼' : '▶'}</span>
              </div>
              {vencidosOpen && (
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--surface2)' }}>
                  {fechasVencidas.map(f => (
                    <GrupoVencimientos key={f} fecha={f} items={grupos[f]} today={today}
                      onOpenEdit={onOpenEdit} onVerTarea={onVerTarea}
                      onNavigateExpediente={onNavigateExpediente} completarTarea={completarTarea} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Próximos (no vencidos) */}
          {fechasFuturas.map(f => (
            <GrupoVencimientos key={f} fecha={f} items={grupos[f]} today={today}
              onOpenEdit={onOpenEdit} onVerTarea={onVerTarea}
              onNavigateExpediente={onNavigateExpediente} completarTarea={completarTarea} />
          ))}
        </div>
      )}
    </div>
  );
}
